import type { IntegrationConfig, IntegrationResult, IntegrationParams } from './types';
import { BaseIntegration } from './core/BaseIntegration';
import { getIntegration } from './registry';

/**
 * Factory for creating and managing integration instances
 */
export class IntegrationFactory {
  private instances: Map<string, BaseIntegration> = new Map();
  private configs: Map<string, IntegrationConfig> = new Map();

  /**
   * Configure an integration (doesn't instantiate yet)
   */
  configure(name: string, config: Omit<IntegrationConfig, 'name'>): void {
    this.configs.set(name, { name, ...config });
  }

  /**
   * Get or create an integration instance
   */
  get(name: string): BaseIntegration {
    // Return existing instance
    if (this.instances.has(name)) {
      return this.instances.get(name)!;
    }

    // Get constructor
    const Constructor = getIntegration(name);
    if (!Constructor) {
      throw new Error(`Unknown integration: ${name}. Make sure it's imported.`);
    }

    // Get config
    const config = this.configs.get(name);
    if (!config) {
      throw new Error(
        `Integration not configured: ${name}. Call configure() first.`,
      );
    }

    // Create instance
    const instance = new Constructor(config);
    this.instances.set(name, instance);

    return instance;
  }

  /**
   * Execute an action on an integration
   */
  async execute(
    integration: string,
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const instance = this.get(integration);
    return await instance.execute(action, params);
  }

  /**
   * Check if integration is configured
   */
  isConfigured(name: string): boolean {
    return this.configs.has(name);
  }

  /**
   * Register an integration instance directly (used by mock infrastructure)
   */
  registerInstance(name: string, instance: BaseIntegration): void {
    this.instances.set(name, instance);
  }

  /**
   * Clear all instances (useful for testing)
   */
  clear(): void {
    this.instances.clear();
  }

  /**
   * Clear all instances and configs
   */
  reset(): void {
    this.instances.clear();
    this.configs.clear();
  }
}

let _factory: IntegrationFactory | null = null;

export function getIntegrationFactory(): IntegrationFactory {
  if (!_factory) {
    _factory = new IntegrationFactory();
  }
  return _factory;
}

export function resetIntegrationFactory(): void {
  _factory?.reset();
  _factory = null;
}
